import { useMemo } from 'react';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { addResearchAssistantMessages } from '@verantos-dev/app-state-research-assistant';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';

import type { CohortRevertedEventData, SocketEventCallbacks } from './useCohortDefinitionSocket';

/**
 * Builds socket callbacks that are not yet covered by Zustand lifecycle hooks:
 * - RA revert/system messages (onCohortReverted)
 *
 * All Redux bridge callbacks (RTK cache patches, setIsSaving, clearLocalUnsavedChanges,
 * clearStagedFromResearchAssistant, clearItemChangeStatuses, setCommittedDefinition,
 * onProfileInvalidate) have been removed — Zustand lifecycle handles those now.
 */
export const useSocketReduxSync = (
  _projectId: string | undefined,
  _cohortId: string | undefined
): SocketEventCallbacks => {
  const dispatch = useAppDispatch();

  return useMemo(
    (): SocketEventCallbacks => ({
      onCohortReverted: (data: CohortRevertedEventData) => {
        if (data.cohortAction === 'revert') {
          dispatch(
            addResearchAssistantMessages([
              {
                message: 'You reverted the unsaved changes',
                timestamp: data.systemMessage?.timestamp || new Date().toISOString(),
                currentUser: '',
                isCurrentUser: false,
                type: ResearchAssistantMessageEnumLegacy.REVERT,
              },
            ])
          );
        } else if (data.systemMessage) {
          dispatch(
            addResearchAssistantMessages([
              {
                message: data.systemMessage.text,
                timestamp: data.systemMessage.timestamp,
                currentUser: 'system',
                isCurrentUser: false,
                type: ResearchAssistantMessageEnumLegacy.SAVE,
              },
            ])
          );
        }
      },
    }),
    [dispatch]
  );
};
